import { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ColDef, DoesFilterPassParams } from 'ag-grid-enterprise'
import type { Dayjs } from 'dayjs'
import { StatementColumn, StatementDataType } from '../../financialStatements/types'
import { EnhancedDriverData } from '../types/driverData'
import { getBaseCellClasses, getBaseColumnProps } from '../../financialStatements/utils/columnUtils'
import DimensionColumnFilter from '../../../shared/components/custom/Grid/Filter/DimensionColumnFilter'
import BudgetDriverColumnFilter from '../../../shared/components/custom/Grid/Filter/BudgetDriverColumnFilter'
import { BudgetDriver } from '../../../types/budgetDriver/BudgetDriver'
import {
  setRegistryValueWithDirtyTracking,
  extractDataValue,
  isCellModified,
  getChangesRegistryKey
} from '../../../shared/utils/balanceRegistry'
import { PercentageCellEditor, NumericCellEditor } from '../../../shared/components/custom/Grid/Editor'
import { formatGridValue } from '../../../shared/components/custom/Grid/columnTypes'
import { getDecimalsFromParams } from '../../../shared/components/custom/Grid/gridUtils'
import { VariableType } from '../../../components/Table/types'

interface UseDriverDataTableColumnsOptions {
  columns: StatementColumn[]
  dimensionMap?: Map<string, string>
  budgetDriverMap?: Map<number, BudgetDriver>
}

type UseDriverDataTableColumnsReturn = ColDef<EnhancedDriverData>[]

const doesDimensionFilterPass = (
  params: DoesFilterPassParams<EnhancedDriverData, any, { filterType: string; values: string[] }>
) => {
  const { node, model } = params

  // If no filter model, show all rows
  if (!model || !model.values || model.values.length === 0) {
    return true
  }

  // Get the dimension ID directly from node data
  const dimensionId = node.data?.dimensionId
  if (!dimensionId) {
    return false
  }

  // Check if the dimension ID is in the selected values
  return model.values.includes(dimensionId)
}

const doesFormulaFilterPass = (
  params: DoesFilterPassParams<EnhancedDriverData, any, { filterType: string; values: number[] }>
) => {
  const { node, model } = params

  // If no filter model, show all rows
  if (!model || !model.values || model.values.length === 0) {
    return true
  }

  // Get the formula ID directly from node data
  const formulaId = node.data?.formulaId
  if (!formulaId) {
    return false
  }

  // Check if the formula ID is in the selected values
  return model.values.includes(formulaId)
}

/**
 * Determines which registry to use based on column type and forecast cursor
 * @param column - The column configuration
 * @param forecastCursor - The forecast cursor date (optional)
 * @returns The registry key to use ('budget' or 'actuals')
 */
export const getRegistryKeyForColumn = (column: StatementColumn, forecastCursor?: Dayjs): 'budget' | 'actuals' => {
  // Actuals columns always use actuals registry
  if (column.dataType === StatementDataType.actuals) {
    return 'actuals'
  }

  // For forecast columns, check if we're before or after the cursor
  if (column.dataType === StatementDataType.forecast && forecastCursor) {
    if (column.year && column.month) {
      const columnDate = forecastCursor
        .clone()
        .year(column.year)
        .month(column.month - 1)
      const useForecastActuals =
        columnDate.isBefore(forecastCursor, 'month') || columnDate.isSame(forecastCursor, 'month')

      if (useForecastActuals) {
        return 'actuals'
      }
    }
  }

  // Default to budget
  return 'budget'
}

export const useDriverDataTableColumns = ({
  columns,
  dimensionMap,
  budgetDriverMap
}: UseDriverDataTableColumnsOptions): UseDriverDataTableColumnsReturn => {
  const { t } = useTranslation()
  // Helper function to create a column definition for a single column
  const createColumnDef = useCallback((column: StatementColumn): ColDef<EnhancedDriverData> => {
    return {
      ...getBaseColumnProps(column),
      editable: params => params.context?.isEditMode === true,
      valueFormatter: formatGridValue,
      cellEditorSelector: params => {
        if (params.data?.variableType === 'percentage') {
          const decimals = getDecimalsFromParams(params, VariableType.percentage)
          return {
            component: PercentageCellEditor,
            params: { allowNegative: false, ...(params.colDef?.cellEditorParams || {}), precision: decimals + 2 }
          }
        }
        const decimals = getDecimalsFromParams(params, VariableType.absolute)
        return {
          component: NumericCellEditor,
          params: { decimals, allowNegative: true, ...(params.colDef?.cellEditorParams || {}) }
        }
      },
      valueGetter: params => {
        const { dataKey, dataSource } = column
        if (!params.data || dataKey === undefined) return null

        const registryKey = getRegistryKeyForColumn(column, params.context?.forecastCursor)
        const changesRegistryKey = getChangesRegistryKey(registryKey)

        // Check if there's a pending change (explicitly check for key existence)
        const changesRegistry = params.data[changesRegistryKey]
        if (changesRegistry?.[dataSource]?.[dataKey] !== undefined) {
          const value = changesRegistry[dataSource][dataKey]
          return value
        }

        // Fall back to original value
        const originalValue = extractDataValue(params.data, column, registryKey)
        return originalValue
      },
      valueSetter: params => {
        if (!params.data) return false

        const { dataKey } = column
        if (dataKey === undefined) return false

        const registryKey = getRegistryKeyForColumn(column, params.context?.forecastCursor)
        const changesRegistryKey = getChangesRegistryKey(registryKey)

        // Use dirty tracking to only store changes
        const result = setRegistryValueWithDirtyTracking(
          params.data,
          column,
          params.newValue,
          registryKey,
          changesRegistryKey
        )
        return result
      },
      cellClass: params => {
        const baseClasses = getBaseCellClasses(params as any, column)
        const classes = Array.isArray(baseClasses) ? baseClasses : [baseClasses]
        classes.push('ag-right-aligned-cell')

        // Add dirty indicator if cell has been modified
        const registryKey = getRegistryKeyForColumn(column, params.context?.forecastCursor)
        const changesRegistryKey = getChangesRegistryKey(registryKey)

        if (isCellModified(params.data, column, changesRegistryKey)) {
          classes.push('cell-modified')
        }

        return classes
      }
    }
  }, [])

  // Memoized column definitions - flat structure like balance sheet
  const columnDefs = useMemo((): ColDef<EnhancedDriverData>[] => {
    const cols: ColDef<EnhancedDriverData>[] = []

    cols.push({
      field: 'formulaId',
      headerName: t('entity:budgetDriver'),
      pinned: 'left',
      width: 200,
      sortable: true,
      filter: {
        component: BudgetDriverColumnFilter,
        doesFilterPass: doesFormulaFilterPass
      },
      cellDataType: 'text',
      enableRowGroup: true,
      useValueFormatterForExport: true,
      valueFormatter: params => {
        if (!params.value) return ''
        const budgetDriver = budgetDriverMap?.get(params.value)
        return budgetDriver?.name ?? String(params.value)
      },
      comparator: (valueA: number, valueB: number) => {
        const budgetDriverA = budgetDriverMap?.get(valueA)
        const budgetDriverB = budgetDriverMap?.get(valueB)
        const nameA = budgetDriverA?.name ?? String(valueA)
        const nameB = budgetDriverB?.name ?? String(valueB)
        return nameA.localeCompare(nameB)
      }
    })

    cols.push({
      field: 'dimensionId',
      headerName: t('global:dimension'),
      pinned: 'left',
      width: 200,
      sortable: true,
      filter: {
        component: DimensionColumnFilter,
        doesFilterPass: doesDimensionFilterPass
      },
      cellDataType: 'text',
      enableRowGroup: true,
      useValueFormatterForExport: true,
      valueFormatter: params => {
        if (!params.value) return ''
        return dimensionMap?.get(params.value) ?? params.value
      },
      comparator: (valueA: string, valueB: string) => {
        const nameA = dimensionMap?.get(valueA) ?? valueA
        const nameB = dimensionMap?.get(valueB) ?? valueB
        return nameA.localeCompare(nameB)
      }
    })

    // cols.push({
    //   field: 'dimensionId'
    // })

    // Add data columns - flat structure (no grouping)
    columns?.forEach((column: StatementColumn) => {
      cols.push(createColumnDef(column))
    })

    return cols
  }, [columns, createColumnDef, dimensionMap, budgetDriverMap, t])

  return columnDefs
}
